import type { WrittenPage } from '../../sections'

/**
 * The New Renaissance e-kitabı — legacy sistemlerin AI çağında modernizasyonu.
 *
 * Bölüm sırası, görseller ve maddeler İngilizcesiyle birebir aynı; değişen
 * yalnızca nasıl söylendiği. E-kitabın adı kapağında basılı olduğu için
 * İngilizce kalıyor; "legacy system", "AI", "agility", "change management" gibi
 * yerleşik terimler de öyle.
 *
 * İndirilen dosya İngilizce — e-kitabın Türkçesi yok. Bu, sayfayı Türkçe
 * yazmamak için bir sebep değil: sayfayı okuyup indirme kararını veren kişi
 * Türkiye'deki okur, ve karar Türkçe veriliyor.
 *
 * Formla ilgili açık soru İngilizce sayfada duruyor ve orada bir kez duruyor:
 * `check` iki dilde tekrarlanınca aynı karar iki not olarak sayılıyor.
 */
const EBOOK_PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
  + 'ec2a18db-5e37-491c-8e1a-dc6d20ec0dc9-Reinventing%20Legacy%20in%20the%20Age%20of%20AI.pdf'

export const ebookLegacySystemModernization: WrittenPage = {
  navLabel: 'The New Renaissance',
  title: 'Reinventing Legacy in the Age of AI',
  description:
    'AI, legacy sistemlerin modernizasyonunu nasıl sürüklüyor, süreçleri nasıl dönüştürüyor ve '
    + 'kültürü nasıl yeniden biçimlendiriyor? Digitopia Impact Labs’in The New Renaissance '
    + 'e-kitabını indirin.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Yeni bir Rönesans',
      headline: 'Reinventing Legacy in the Age of AI',
      body: [
        'AI’ın legacy sistemlerin modernizasyonunu nasıl sürüklediğini, süreçleri nasıl '
          + 'dönüştürdüğünü ve iş başarısı için kültürü nasıl yeniden biçimlendirdiğini görün.',
      ],
      ctas: [{ label: 'İndirin', href: EBOOK_PDF }],
      photo: {
        src: '/photo/legacy-modernization-ebook-artwork.webp',
        alt:
          'E-kitabın kapak görseli: sağ yarısı parlayan mavi devre şemalarına dönüşen mermer bir '
          + 'antik büst, arkasında turuncu ışıkla çizilmiş figürler.',
      },
      photoFrame: 'square',
    },
    {
      kind: 'prose',
      align: 'center',
      paragraphs: [
        'AI çağı başladı ve şirketlerin işleyişini baştan tanımlıyor. Legacy sistemler, zamanı '
        + 'geçmiş süreçler ve katı yönetim yapıları şirketleri geride tutuyor. Bu e-kitap, '
        + 'yeniliği, çevikliği ve amaç odaklı büyümeyi güçlendiren bir Rönesans zihniyetini '
        + 'benimsemek için elinizdeki rehber.',
      ],
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Bu e-kitabı neden indirmelisiniz?',
      sub: 'Geleceğe uyum sağlamakla yetinmeyin, ona siz öncülük edin.',
      body: ['Şunları göreceksiniz:'],
      points: [
        'Zekânın sürüklediği bir dünyada legacy sistemler neden bir yük?',
        'Kurumları modernleşmeye ve büyümeye taşıyan ilkeler neler?',
        'Dönüşümün yalnızca mümkün değil, zorunlu olduğunu gösteren sahadan vaka çalışmaları.',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: EBOOK_PDF }],
      photo: {
        src: '/photo/legacy-modernization-ebook-covers.webp',
        alt:
          'The New Renaissance’ın iki kopyası, biri diğerinin arkasında, beyaz zeminde.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Neler öğreneceksiniz?',
      points: [
        'İşin Rönesansı: legacy sistemleri neden yeniden düşünmek gerekiyor ve şirketler AI’ın '
          + 'dönüştürücü gücünü nasıl sahipleniyor?',
        'Liderliği yeniden tanımlamak: modern yönetim tarzları yeniliği beslemek ve en iyi '
          + 'yetenekleri çekmek için neden kritik?',
        'Saklı değeri açığa çıkarmak: veri neden AI çağının can damarı ve ondan nasıl '
          + 'yararlanılır?',
        'Süreçleri çeviklik için yeniden kurmak: zamanı geçmiş operasyonları uyum yeteneği '
          + 'yüksek, müşteri odaklı modellere dönüştürün.',
        'Kültürel dönüşüm: kalıcı değişimin merkezinde neden insanın ve kültürün modernleşmesi '
          + 'var?',
        'Change management’te ustalaşmak: dönüşümü amaçla ve insanı merkeze alarak sürüklemek '
          + 'için uygulanabilir stratejiler.',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: EBOOK_PDF }],
      photo: {
        src: '/photo/legacy-modernization-ebook-stack.webp',
        alt: 'The New Renaissance’ın basılı kopyalarından oluşan bir yığın, yukarıdan görünüm.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Legacy sistem modernizasyonu yolculuğunuza bugün başlayın',
      sub:
        'The New Renaissance: Reinventing Legacy in the Age of AI’ın ücretsiz kopyasını indirin; '
        + 'sistemleri, süreçleri ve kültürü nasıl modernleştireceğinizi görün.',
      cta: { label: 'E-kitabı indirin', href: EBOOK_PDF },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'Müşterilerimiz ne diyor?',
      items: [
        {
          quote:
            'A101’de son 2 yıldır dijital dönüşümle meşgulüz; müşteri deneyimi, operasyonel '
            + 'iyileştirmeler, inovasyon ve dijital kültür gibi farklı boyutlara odaklanıyoruz. '
            + 'Dijitalleşme günlük hayatın her alanında yaşanıyor. Biz de buna uyum sağlamak ve '
            + 'ülke dinamiklerini, perakende sektörünün dinamiklerini ve kurum kültürümüzü '
            + 'gözeten bir yol haritası kurmak istiyoruz. Sürecin en önemli parçalarından biri, '
            + 'nerede olduğumuzu net biçimde görmemizi ve önceliklerimizi nesnel olarak '
            + 'belirlememizi sağlayan DMI Dijital Olgunluk Endeksi oldu.',
          source: 'Orhan Dağlıoğlugil, CTO',
          company: 'A101',
          logo: { name: 'A101', src: '/logo/a101card.png' },
          portrait: {
            src: '/photo/portrait-orhan-daglioglugil.png',
            alt: 'Orhan Dağlıoğlugil',
          },
        },
      ],
    },
  ],
}
